import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { EditableText } from '@/hooks/useEditableContent';
import { Brain, Heart, Moon, Sparkles, Activity, Shield, CheckCircle2, Loader2, Eye, Star, BookOpen, Users, Gem, Utensils, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';
import therappiLogo from '@/assets/therappi-logo.svg';
import therappiWhoopLogo from '@/assets/therappi-whoop-combined.svg';
import therappiTextLogo from '@/assets/therappi-text-logo.svg';
import phoneCheckinMockup from '@/assets/phone-checkin-dynamic.png';
import phoneTherapyMockup from '@/assets/phone-therapy-dynamic.png';
import phoneFoodMockup from '@/assets/phone-food-dynamic.png';
import phoneBiodataMockup from '@/assets/phone-biodata-dynamic.png';
import phoneVisionMockup from '@/assets/phone-vision-dynamic.png';
const LandingPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // WHOOP Pioneer Program form state
  const [whoopEmail, setWhoopEmail] = useState('');
  const [isWhoopSubmitting, setIsWhoopSubmitting] = useState(false);
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('waitlist-signup', {
        body: {
          name,
          email
        }
      });
      if (error) {
        console.error('Function invocation error:', error);
        if (error.message?.includes('already on the waitlist')) {
          toast.error('This email is already on our waitlist!');
        } else {
          toast.error(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }
      if (data?.error) {
        if (data.error.includes('already on the waitlist')) {
          toast.error('This email is already on our waitlist!');
        } else {
          toast.error(data.error);
        }
      } else {
        toast.success('Thank you for joining our waitlist! Check your email for confirmation.');
        setName('');
        setEmail('');
      }
    } catch (error: any) {
      console.error('Waitlist signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleWhoopPioneerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!whoopEmail) {
      toast.error('Please provide your email');
      return;
    }
    setIsWhoopSubmitting(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('waitlist-signup', {
        body: {
          name: 'WHOOP Pioneer',
          email: whoopEmail,
          notes: 'WHOOP Pioneer Program Participant'
        }
      });
      if (error) {
        console.error('Function invocation error:', error);
        if (error.message?.includes('already on the waitlist')) {
          toast.error('This email is already registered!');
        } else {
          toast.error(error.message || 'Something went wrong. Please try again.');
        }
        return;
      }
      if (data?.error) {
        if (data.error.includes('already on the waitlist')) {
          toast.error('This email is already registered!');
        } else {
          toast.error(data.error);
        }
      } else {
        toast.success('Welcome to the Pioneer Program! Check your email for confirmation.');
        setWhoopEmail('');
      }
    } catch (error: any) {
      console.error('WHOOP signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsWhoopSubmitting(false);
    }
  };
  const scrollToWaitlist = () => {
    const waitlistSection = document.getElementById('waitlist-section');
    waitlistSection?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const scrollToPioneer = () => {
    const pioneerSection = document.getElementById('pioneer-section');
    pioneerSection?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  return <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5">
      <SEO
        title="Therappi® - Whole-Self Wellbeing Across the Levels of Self"
        description="QuantumSelf Psychology™, self-reflection tools, biometrics and AI guidance - one platform supporting every level of the self, across time."
        path="/landing"
      />

      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-start">
            <img 
              src={therappiLogo} 
              alt="Therappi® Logo" 
              className="h-24 sm:h-32 md:h-48 object-contain cursor-pointer" 
              onClick={() => navigate('/auth')}
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-3 sm:px-6 py-8 sm:py-16 md:py-24">
        <div className="max-w-5xl mx-auto text-center">
          {/* Coming Soon Banner */}
          <div className="mb-4 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-full border border-primary/30 backdrop-blur-sm">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary animate-pulse flex-shrink-0" />
              <p className="text-sm sm:text-lg font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Coming Soon...
              </p>
            </div>
          </div>

          {/* Main Hero Logo - Large Focal Point */}
          <div className="mb-4 sm:mb-8 mt-2 sm:mt-8 flex justify-center">
            <img src={therappiTextLogo} alt="Therappi® - Where Science Meets Soul" className="w-full max-w-[220px] sm:max-w-xl md:max-w-2xl lg:max-w-3xl" />
          </div>

          {/* Hero Statement - Inspirational & Motivational */}
          <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
            Your body already knows the answers.<br />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Therappi<sup className="text-[0.6em]">®</sup> helps you hear them.</span>
          </h1>

          <p className="text-sm sm:text-lg md:text-xl text-muted-foreground mb-4 sm:mb-8 leading-relaxed">
            Tired of drowning in data without direction? Your wearable shows recovery scores, your period tracker logs dates, your meditation app counts minutes-but none of them talk to each other. None of them show you the invisible threads connecting your sleep to your mood, your cycle to your performance, your anxiety to your HRV.
          </p>

          <p className="text-sm sm:text-lg md:text-xl mb-4 sm:mb-8 leading-relaxed font-medium text-primary">
            Therappi<sup className="text-[0.6em]">®</sup> is the first platform that doesn't just track your life-it understands it. Then it shows you exactly what to do about it.
          </p>

          {/* Dramatic Proof Point */}
          <div className="mb-6 sm:mb-12">
            <Card className="p-4 sm:p-8 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/30">
              <p className="text-sm sm:text-lg md:text-xl leading-relaxed">
                Early pilot members report noticing shifts in mood, energy and cycle-related patterns earlier than they used to, and beginning to see how choices such as alcohol, sleep and stress show up in the following day's wellbeing. The difference? Therappi<sup className="text-[0.6em]">®</sup> helps you connect dots other apps do not surface.
              </p>
              <p className="text-xs text-muted-foreground mt-3 sm:mt-4">*Based on qualitative reports from early pilot testers. Individual experience will vary. Therappi<sup className="text-[0.5em]">®</sup> is a wellbeing self-reflection tool and is not a medical device, diagnostic tool, or a substitute for professional medical or mental health care.</p>
            </Card>
          </div>

          {/* WHOOP Integration Highlight */}
          <div className="flex flex-col items-center justify-center gap-2 sm:gap-4 mb-6 sm:mb-12">
            <div className="flex items-center justify-center">
              <img src={therappiWhoopLogo} alt="Therappi × WHOOP" className="h-14 sm:h-20 md:h-24 lg:h-32 object-contain" />
            </div>
            <p className="text-sm sm:text-base md:text-lg text-secondary font-medium">
              The first platform where your biometrics meet psychological wisdom and spiritual insight-showing that longevity isn't just about physical health, it's about caring for all dimensions of who you are
            </p>
          </div>

          {/* CTA Button - Single clear action */}
          <div className="flex justify-center">
            <Button 
              variant="default" 
              size="lg" 
              onClick={scrollToWaitlist} 
              className="w-full sm:w-auto text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-8 hover:scale-105 transition-transform shadow-xl font-semibold bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
            >
              Join the Beta Waitlist
            </Button>
          </div>
        </div>
      </section>

      {/* Main Value Section */}
      <section className="container mx-auto px-3 sm:px-6 py-10 sm:py-24">
        <div className="max-w-6xl mx-auto">
          {/* Accent divider */}
          <div className="flex justify-center mb-6 sm:mb-10">
            <div className="h-1 w-20 bg-gradient-to-r from-primary to-accent rounded-full" />
          </div>
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">This is more than a tracking app - it's <span className="text-primary">Personalised Transformation</span>.</h2>
            <p className="text-sm sm:text-xl text-muted-foreground">
              Therappi<sup className="text-[0.6em]">®</sup> weaves together your biometrics, hormonal rhythms, sleep architecture, emotional patterns, and daily behaviours into a living map of your system. Then - and this is where everything changes - it gives you personalised reflection prompts precisely when you need them, using the <span className="text-secondary font-medium">Soulprint Sequence™: Regulate, Recognise, Reflect, Release, Reprogramme, Reconnect</span>.
            </p>
          </div>

          {/* AI Reflective Support - Lead Section with Phone Mockup */}
          <div className="mb-12 sm:mb-20">
            <div className="grid md:grid-cols-2 gap-6 lg:gap-12 items-center">
              {/* Phone Mockup */}
              <div className="order-2 md:order-1 flex justify-center">
                <img src={phoneTherapyMockup} alt="Therappi® AI Reflective Support Interface" className="w-full max-w-[280px] sm:max-w-sm rounded-3xl shadow-2xl" />
              </div>
              
              {/* Content */}
              <div className="order-1 md:order-2">
                <h2 className="text-xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
                  AI-Powered Reflective Support That <span className="text-primary">Helps You See More</span>
                </h2>
                <p className="text-base sm:text-xl mb-3 sm:mb-4 font-semibold text-secondary">
                  Your Timeline Reveals Your Patterns
                </p>
                <p className="text-sm sm:text-lg text-muted-foreground mb-4 sm:mb-6">
                  We all have blindspots - patterns we repeat, relationships we return to, behaviours we choose not to see. Unlike AI chatbots that forget your history, Therappi<sup className="text-[0.6em]">®</sup> builds a continuous personal timeline across weeks and months, so reflection becomes cumulative.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="h-5 w-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-sm sm:text-base">Pattern Recognition Across Time</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      "This is the fourth time you have blamed yourself in a conflict." The AI surfaces patterns across your entries that single conversations do not reveal.
                    </p>
                  </div>
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="h-5 w-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-sm sm:text-base">The Sanctuary: Your Complete History</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Every conversation stored, searchable, reviewable - building a continuous personal reflection timeline.
                    </p>
                  </div>
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="h-5 w-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-sm sm:text-base">Multi-Modal Reflective Frameworks</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Choose your reflection style: Person-Centred, CBT-inspired, psychodynamic, DBT-inspired, ACT-inspired, or QuantumSelf Psychology™. These are self-reflection frameworks, not clinical therapy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* The Sanctuary - Evidence-Based Crisis Tools + Timeline */}
          <div className="mb-12 sm:mb-20">
            <Card className="p-4 sm:p-8 md:p-12 bg-gradient-to-br from-accent/10 via-primary/5 to-accent/10 border-accent/30">
              <div className="flex flex-col items-center justify-center mb-4 sm:mb-6">
                <div className="flex items-center mb-3 sm:mb-4">
                  <Shield className="h-8 w-8 sm:h-12 sm:w-12 text-primary mr-3 sm:mr-4 flex-shrink-0" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">
                    The Sanctuary
                  </h2>
                </div>
                <Badge className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2" variant="secondary">
                  <BookOpen className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  Your Personal Timeline + Self-Support Tools
                </Badge>
              </div>
              <p className="text-sm sm:text-lg text-center text-muted-foreground mb-6 sm:mb-8">
                Founded by a Chartered Counselling Psychologist (registration available on request). The Sanctuary is where your reflection journey lives - every conversation archived, every pattern surfaced. It supports self-reflection and does not replace clinical therapy or crisis services.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-background/50 p-3 sm:p-6 rounded-lg border-2 border-primary/30">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                    <BookOpen className="h-4 w-4 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                    <h3 className="font-semibold text-primary text-xs sm:text-base">Your Timeline</h3>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Every reflective conversation stored, searchable, and reviewable.
                  </p>
                </div>
                <div className="bg-background/50 p-3 sm:p-6 rounded-lg">
                  <h3 className="font-semibold mb-1.5 sm:mb-2 text-primary text-xs sm:text-base">Signposting & Safety Info</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Personal safety planning prompts and signposting to external crisis services. Not a crisis service itself.
                  </p>
                </div>
                <div className="bg-background/50 p-3 sm:p-6 rounded-lg">
                  <h3 className="font-semibold mb-1.5 sm:mb-2 text-primary text-xs sm:text-base">In-the-Moment Toolkit</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Grounding exercises, breathing sequences and self-soothing practices inspired by evidence-based approaches.
                  </p>
                </div>
                <div className="bg-background/50 p-3 sm:p-6 rounded-lg">
                  <h3 className="font-semibold mb-1.5 sm:mb-2 text-primary text-xs sm:text-base">Pattern Recognition</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    The AI helps surface personal patterns over time so you can reflect on them earlier.
                  </p>
                </div>
              </div>
              <div className="bg-primary/20 border border-primary/30 rounded-lg p-4 sm:p-6">
                <p className="text-center text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2 text-primary">Why The Sanctuary Changes Everything</p>
                <p className="text-center text-xs sm:text-sm text-muted-foreground">
                   Other AI wellness apps reset with every conversation. The Sanctuary remembers everything-your history is your power.
                </p>
              </div>
            </Card>
          </div>

          {/* WHOOP × Biometric Intelligence with Phone Mockup */}
          <div className="mb-12 sm:mb-20">
            <div className="grid md:grid-cols-2 gap-6 lg:gap-12 items-center">
              {/* Content */}
              <div>
                <div className="flex justify-start mb-3 sm:mb-4">
                  <img src={therappiWhoopLogo} alt="Therappi × WHOOP" className="h-12 sm:h-16 md:h-20 object-contain" />
                </div>
                <Badge className="mb-3 sm:mb-4 text-xs sm:text-sm px-3 sm:px-4 py-1" variant="outline">
                  <Activity className="h-3 w-3 mr-1.5 sm:mr-2 flex-shrink-0" />
                  The only AI wellness companion that knows your body as well as your mind
                </Badge>
                <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
                  Your Body <span className="text-primary">Knows the Truth</span>
                </h2>
                <p className="text-sm sm:text-lg text-muted-foreground mb-4 sm:mb-8">
                  Connect your WHOOP or wearable to add another layer of truth to your self-care. When you say "I am fine" but your HRV has dropped 40 points. Your body does not lie-and now your reflection space integrates what your body knows.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
                        <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold">When Words and Body Disagree</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Your body reveals what your mind minimises-and Therappi<sup className="text-[0.6em]">®</sup> integrates both.
                    </p>
                  </div>
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
                        <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold">Mind-Body Pattern Recognition</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      See how meditation shifts your nervous system, how emotional regulation shows up in biometrics.
                    </p>
                  </div>
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="h-8 w-8 sm:h-10 sm:w-10 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
                        <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold">Biometrically-Informed Interventions</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Breathwork protocols adjust based on your current HRV-personalised to your body.
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Mockup */}
              <div className="flex justify-center">
                <img src={phoneBiodataMockup} alt="Therappi® WHOOP Integration Interface" className="w-full max-w-[280px] sm:max-w-sm rounded-3xl shadow-2xl" />
              </div>
            </div>
          </div>

          {/* Predictive Wellness Intelligence with Phone Mockup */}
          <div className="mb-12 sm:mb-20">
            {/* Accent divider */}
            <div className="flex justify-center mb-6 sm:mb-10">
              <div className="h-1 w-20 bg-gradient-to-r from-secondary to-accent rounded-full" />
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-12 items-center">
              {/* Content */}
              <div>
                <h2 className="text-xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
                  <span className="text-secondary">Reflective</span> Wellbeing Insights
                </h2>
                <p className="text-sm sm:text-lg text-muted-foreground mb-4 sm:mb-6">
                  Eight-dimensional daily tracking that helps you notice shifts in energy, mood and stress earlier - so you can respond, not react. <span className="text-primary font-medium">Reflection is the new paradigm of self-care</span>.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <h3 className="font-semibold mb-1.5 sm:mb-2 text-primary text-sm sm:text-base">Behavioural Pattern Reflection</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Reflect on how processed food, exercise, hydration, sunlight, alcohol and caffeine appear alongside your mood, energy and wellbeing.
                    </p>
                  </div>
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-secondary/30">
                    <h3 className="font-semibold mb-1.5 sm:mb-2 text-secondary text-sm sm:text-base">Early Awareness Prompts</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Therappi<sup className="text-[0.6em]">®</sup> highlights shifts in your daily patterns so you can notice them earlier and choose how to respond.
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Mockup */}
              <div className="flex justify-center">
                <img src={phoneCheckinMockup} alt="Therappi® Daily Check-in Interface" className="w-full max-w-[280px] sm:max-w-sm rounded-3xl shadow-2xl" />
              </div>
            </div>
          </div>

          {/* Food-Mood Mirror™ with Phone Mockup */}
          <div className="mb-12 sm:mb-20">
            <div className="grid md:grid-cols-2 gap-6 lg:gap-12 items-center">
              {/* Phone Mockup */}
              <div className="order-2 md:order-1 flex justify-center">
                <img src={phoneFoodMockup} alt="Therappi® Food-Mood Mirror Interface" className="w-full max-w-[280px] sm:max-w-sm rounded-3xl shadow-2xl" />
              </div>
              
              {/* Content */}
              <div className="order-1 md:order-2">
                <div className="flex items-center mb-4 sm:mb-6">
                  <Brain className="h-8 w-8 sm:h-12 sm:w-12 text-primary mr-3 sm:mr-4 flex-shrink-0" />
                  <h2 className="text-xl sm:text-3xl md:text-4xl font-bold">
                    The Food-Mood Mirror™
                  </h2>
                </div>
                <p className="text-base sm:text-xl mb-3 sm:mb-4 font-semibold text-secondary">
                  Scan Your Meal. See Not Just Calories - But Consciousness.
                </p>
                <p className="text-sm sm:text-lg text-muted-foreground mb-4 sm:mb-6">
                  Discover how your choices affect your mood, microbiome, energy, and emotional equilibrium. This isn't a diet function-it's a therapeutic awareness tool.
                </p>
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  <div className="bg-background/50 p-3 sm:p-4 rounded-lg border border-primary/20">
                    <h3 className="font-semibold mb-1.5 sm:mb-2 text-primary text-sm sm:text-base">Scientific Foundation</h3>
                    <ul className="text-xs sm:text-sm text-muted-foreground space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-accent">•</span>
                        <span><strong>Gut-Brain Axis:</strong> Bidirectional communication via serotonin & GABA</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-accent">•</span>
                        <span><strong>Psychoneuroimmunology:</strong> Diet, inflammation & emotional regulation</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Part of the <strong className="text-secondary">Body Level</strong> in your Hierarchy of Healing<sup className="text-[0.6em]">®</sup>
                </p>
              </div>
            </div>
          </div>

          {/* The 5 Dimensions of Self - Deep Dive */}
          <div className="mb-12 sm:mb-20">
            {/* Accent divider */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="h-1 w-20 bg-gradient-to-r from-primary to-secondary rounded-full" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-4 sm:mb-6">The <span className="text-primary">Five Dimensions</span> of Self</h2>
            <p className="text-sm sm:text-lg text-muted-foreground text-center mb-8 sm:mb-12">
              True transformation happens when you heal all dimensions of who you are. Therappi<sup className="text-[0.6em]">®</sup> integrates body, heart, mind, soul, and relationships-because longevity isn't just physical health. It's wholeness in every dimension of your existence.
            </p>

            {/* BODY */}
            <Card className="p-4 sm:p-8 mb-6 sm:mb-8 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="h-12 w-12 sm:h-16 sm:w-16 bg-gradient-primary rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <Activity className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold">Body: Your Biological Foundation</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">Where physiology meets psychology</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">1. Wearable Integration (WHOOP®)</h4>
                  <p className="text-sm text-muted-foreground">
                    Real-time HRV, recovery, sleep architecture, and strain data flows into your wellbeing insights. See how meditation shifts your nervous system, how reflective practice supports recovery scores, how emotional regulation shows up in biometrics. Statistical correlations reveal mind-body connections other apps cannot see.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">2. Gut-Brain Nutrition Tracking</h4>
                  <p className="text-sm text-muted-foreground">
                    Track how processed food, hydration, caffeine, and alcohol influence mood, energy, and mental clarity. Receive personalised insights based on your unique patterns-not generic diet advice. Understand the bidirectional communication between your gut microbiome and mental health.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">3. Sleep Architecture Analysis</h4>
                  <p className="text-sm text-muted-foreground">
                    Beyond sleep duration-analyse sleep quality, deep sleep cycles, REM patterns, and wake-ups. Correlate sleep data with mood, performance, and HRV. Optimise rest with lunar cycle awareness and receive guided guided relaxation for deeper healing.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">4. Cycle & Hormonal Intelligence</h4>
                  <p className="text-sm text-muted-foreground">
                    Track menstrual cycles, perimenopause, menopause, pregnancy, postpartum, and IVF journeys with psychological integration. Understand how hormonal shifts drive mood, energy, sleep, and behavior. Receive phase-specific interventions and lunar synchronisation insights.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">5. Movement & Strain Optimisation</h4>
                  <p className="text-sm text-muted-foreground">
                    Align exercise intensity with your recovery state, cycle phase, and emotional bandwidth. Receive data-driven recommendations for rest, active recovery, or peak performance training-tailored to your body's current capacity, not a generic workout plan.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">6. Inflammation & IL Score Tracking</h4>
                  <p className="text-sm text-muted-foreground">
                    Monitor your Inflammation Load Score through daily behaviours, sleep, stress, and nutrition patterns. Understand how chronic inflammation impacts mood, energy, and longevity. Receive anti-inflammatory protocols and see correlations with mental health markers.
                  </p>
                </div>
              </div>
            </Card>

            {/* HEART */}
            <Card className="p-8 mb-8 bg-gradient-to-br from-accent/5 to-primary/5">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center mr-4">
                  <Heart className="h-8 w-8 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Heart: Your Emotional & Nervous System</h3>
                  <p className="text-sm text-muted-foreground">Regulating the body's central command system</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">1. HRV-Guided Breathwork</h4>
                  <p className="text-sm text-muted-foreground">
                    Real-time HRV biofeedback directs breathwork protocols-box breathing for focus, coherence breathing for calm, extended exhale for nervous system reset. Watch your HRV shift in response, showing the shift with each practice. Move from dysregulation to balance in minutes.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">2. Somatic Release Practices</h4>
                  <p className="text-sm text-muted-foreground">
                    EFT tapping protocols, progressive muscle relaxation, butterfly hug (bilateral stimulation), and trauma-informed body-based techniques. Release stored tension and emotional energy held in the body-moving from freeze to flow, from reactive to responsive.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">3. Emotional Pattern Tracking</h4>
                  <p className="text-sm text-muted-foreground">
                    Eight-dimensional daily check-ins log mood, anxiety, energy, sleep quality, social connection and more. The AI surfaces emotional trends over time and offers personalised reflection prompts.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">4. Polyvagal-Inspired Practices</h4>
                  <p className="text-sm text-muted-foreground">
                    Understand your nervous system states (ventral vagal, sympathetic, dorsal vagal) and explore practices that may help you shift between them - vagal toning exercises, co-regulation practices and nervous system literacy.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">5. Heart Coherence Training</h4>
                  <p className="text-sm text-muted-foreground">
                    Learn to synchronise heart, breath and brain into a coherent rhythm. Research on coherence practices suggests benefits for stress and emotional regulation. Track your practice alongside HRV and subjective wellbeing over time.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">6. Meditation Medication® Library</h4>
                  <p className="text-sm text-muted-foreground">
                    Access 60+ guided meditations for every need-anxiety relief, deep sleep, trauma processing, manifestation, and nervous system reset. Each meditation tracks before/after emotional states and integrates with HRV data for measurable impact.
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-8 mb-8 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center mr-4">
                  <Brain className="h-8 w-8 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Mind: Your Cognitive & Therapeutic Intelligence</h3>
                  <p className="text-sm text-muted-foreground">Where thoughts shape reality</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">1. Multi-Modal AI Reflection</h4>
                  <p className="text-sm text-muted-foreground">
                    Choose your reflection style: Person-Centred (reflection, self-discovery), CBT-inspired (rewiring thought patterns), psychodynamic (exploring unconscious roots), DBT-inspired (distress tolerance, emotional regulation), ACT-inspired (values-based action), or QuantumSelf Psychology™ (a complementary practice integrating science, soul and quantum principles). Your AI companion remembers your story, learns your patterns and adapts to your needs. This is a self-reflection tool, not clinical therapy.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">2. Past Self Timeline & the Soulprint Sequence™</h4>
                  <p className="text-sm text-muted-foreground">
                    Map pivotal moments from your past to understand how they shape your present. Move through the Soulprint Sequence™ - Regulate the nervous system, Recognise the pattern, Reflect on its origin, Release the old imprint, Reprogramme a new one, and Reconnect as a whole self - to turn old wounds into wisdom through structured self-reflection.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">3. Quantum Journal with AI Insights</h4>
                  <p className="text-sm text-muted-foreground">
                    Write to process, release, and integrate. Therappi's AI analyses patterns across your entries-spotting themes, tracking emotional shifts, revealing insights you might miss. Your complete personal archive, searchable and meaningful, showing how far you've come.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">4. Cognitive Reframing Tools</h4>
                  <p className="text-sm text-muted-foreground">
                    Challenge cognitive distortions (catastrophising, black-and-white thinking, mind reading) with structured CBT exercises. Build evidence-based alternative thoughts. Track how reframing impacts mood and behavior over time-measurable cognitive flexibility.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">5. Mental Wellbeing Awareness</h4>
                  <p className="text-sm text-muted-foreground">
                    The AI helps you notice shifts in mood, energy, biometrics and sleep over time, combining signals that are hard to see in single-purpose apps. You receive reflection prompts and self-support suggestions, not clinical interventions.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">6. Thought Tracker & CBT Toolkit</h4>
                  <p className="text-sm text-muted-foreground">
                    Capture negative automatic thoughts in real-time and work through structured CBT exercises. Identify cognitive distortions, build balanced alternatives, and track your cognitive flexibility over time with measurable improvement metrics.
                  </p>
                </div>
              </div>
            </Card>

            {/* SOUL */}
            <Card className="p-8 mb-8 bg-gradient-to-br from-accent/5 to-primary/5">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center mr-4">
                  <Sparkles className="h-8 w-8 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Soul: Your Spiritual & Energetic Essence</h3>
                  <p className="text-sm text-muted-foreground">Where intuition meets intention</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">1. Future Self Architecture</h4>
                  <p className="text-sm text-muted-foreground">
                    Build your Future Self Blueprint-who you're becoming, not just what you want. Map your values, visualise your future identity, embody that frequency now. Take aligned action daily with AI-guided prompts. This isn't goal-setting-it's identity-based reflection inspired by neuroscience research on how habits and identity change over time.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">2. Manifestation Magnifier</h4>
                  <p className="text-sm text-muted-foreground">
                    Create personalised visualisation movies from your images, affirmations, and vision. Watch them daily to reprogramme your subconscious mind-aligning behavior with aspiration. Rooted in neuroscience, not wishful thinking. Track how visualisation impacts mood, motivation, and action-taking through biometric and emotional data.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">3. Lunar & Astrological Intelligence</h4>
                  <p className="text-sm text-muted-foreground">
                    Sync with lunar phases for optimal manifestation, release, and rest. Track how moon cycles correlate with your mood, energy, and cycle. Align your spiritual practices with natural rhythms-new moon intentions, full moon release, waning moon reflection. Statistical insights reveal your unique lunar patterns.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">4. Oracle & Shadow Work Integration</h4>
                  <p className="text-sm text-muted-foreground">
                    Pull digital oracle cards for guidance, then integrate insights into reflective processing. Use shadow work exercises to explore hidden aspects of self. AI helps you identify recurring oracle themes and connect them to emotional patterns, dreams, and personal breakthroughs-spirituality grounded in psychological depth.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">5. Dream Interpretation & Sleep Wisdom</h4>
                  <p className="text-sm text-muted-foreground">
                    Journal dreams and receive AI-powered interpretation rooted in Jungian psychology and symbolism. Identify recurring dream themes and correlate them with emotional states, life events, and therapeutic work. Your subconscious speaks while you sleep-Therappi helps you listen and integrate the messages.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">6. Rock Remedies® Crystal Healing</h4>
                  <p className="text-sm text-muted-foreground">
                    Explore 24 healing crystals mapped to chakras, emotions, moon phases, and reflection needs. Learn the psychology behind crystal work-grounded in symbolism, intention-setting, and mindfulness. Track which crystals support your reflection journey and receive personalised recommendations.
                  </p>
                </div>
              </div>
            </Card>

            {/* RELATIONSHIPS - The 5th Dimension */}
            <Card className="p-8 mb-8 bg-gradient-to-br from-primary/5 to-accent/5 border-2 border-primary/30">
              <div className="flex items-center mb-6">
                <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center mr-4">
                  <Heart className="h-8 w-8 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Relationships: Your Connection to Self & Others</h3>
                  <p className="text-sm text-muted-foreground">Where isolation transforms into intimacy</p>
                </div>
              </div>
              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-center font-semibold">
                  The missing dimension in most healing apps. Your relationship with yourself and others shapes your entire experience-yet rarely gets tracked or integrated. Until now.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">1. Self-Relationship Tracking</h4>
                  <p className="text-sm text-muted-foreground">
                    Monitor your inner dialogue, self-compassion levels, and self-trust over time. AI detects when self-criticism spikes, when self-sabotage patterns emerge, when you're operating from wounded parts. Correlate with HRV, sleep, and mood to see how self-relationship impacts physiology.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">2. Relational Pattern Recognition</h4>
                  <p className="text-sm text-muted-foreground">
                    Track relationship quality across romantic partnerships, family, friends, and community. AI identifies recurring patterns-conflict avoidance, codependency, withdrawal, people-pleasing-and shows how they correlate with biometric stress signals. Your body knows when relationships deplete you.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">3. Attachment & Connection Insights</h4>
                  <p className="text-sm text-muted-foreground">
                    Understand your attachment style and how it shows up in relationships. Track intimacy levels, emotional safety, trust, and authenticity over weeks and months. See patterns you've normalised-and how they impact your wellbeing, recovery scores, and emotional regulation.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">4. Relationship-Body Correlation</h4>
                  <p className="text-sm text-muted-foreground">
                    Discover how relationship dynamics affect your physiology. "Partner conflict disrupts your sleep quality by 40%." "Low friend support correlates with 6-point HRV drop." "Self-compassion days show 30% better recovery." Data-driven relationship insights, not guesswork.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">5. Relationship Reflection</h4>
                  <p className="text-sm text-muted-foreground">
                    Reflect on relationship challenges with an AI companion that remembers your relational history - past experiences, recurring patterns, growth areas. Explore boundaries, communication, conflict and intimacy with prompts inspired by attachment theory and relational psychology. A self-reflection tool, not couples therapy.
                  </p>
                </div>
                <div className="bg-background/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-primary">6. Accountability Partners & Challenges</h4>
                  <p className="text-sm text-muted-foreground">
                    Invite accountability buddies to share your wellness journey. Track positive behaviours together, support each other through challenges, and celebrate wins. Join group challenges for meditation streaks, movement goals, and habit transformation-social support meets personalised tracking.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Three Key Value Props - Inspirational */}
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {/* Deep Insight Into Your Patterns */}
            <div className="text-center">
              <div className="mb-6 relative aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                <Activity className="h-20 w-20 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">See the invisible connections</h3>
              <p className="text-muted-foreground leading-relaxed">
                Why does your mood crash on day 24 of your cycle? How does that meditation practice actually show up in your HRV? What happens to your emotional regulation when you skip breakfast, or sleep less than 7 hours, or have wine with dinner? Therappi reveals the patterns you've been living but couldn't see-so you can finally optimise what actually matters.
              </p>
            </div>

            {/* Individualised Intervention */}
            <div className="text-center">
              <div className="mb-6 relative aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                <Shield className="h-20 w-20 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Notice shifts earlier</h3>
              <p className="text-muted-foreground leading-relaxed">
                Therappi<sup className="text-[0.6em]">®</sup> helps you notice patterns in mood, energy, sleep and cycle earlier. You get personalised prompts - breathwork for your nervous system, reflective prompts for your emotional state, cycle-informed rest suggestions - so you can respond sooner. Therappi<sup className="text-[0.6em]">®</sup> is not a crisis service. In an emergency contact 999 or Samaritans on 116 123.
              </p>
            </div>

            {/* Optimise Every Dimension */}
            <div className="text-center">
              <div className="mb-6 relative aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                <Sparkles className="h-20 w-20 text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Build sustainable longevity</h3>
              <p className="text-muted-foreground leading-relaxed">
                Align your manifestation work with your luteal phase. Time intense workouts to your follicular surge. Choose the right therapy mode for your current hormonal state. Watch how alcohol consumption naturally decreases when you see its impact on tomorrow's mood and HRV. Everything is personalised to your unique biology, psychology, and energy-so the changes stick, and your future self thrives.
              </p>
            </div>
          </div>

          {/* WHOOP × Therappi Integration */}
          <div className="mb-20">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10">
              <div className="flex justify-center mb-6">
                <img src={therappiWhoopLogo} alt="Therappi × WHOOP" className="h-16 md:h-20 object-contain" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6">
                One Platform. Infinite Integration.
              </h2>
              <p className="text-lg text-center text-muted-foreground mb-12 max-w-3xl mx-auto">
                Your wearable shows you what's happening in your body. Therappi shows you why-and what to do about it. The mind-body connection isn't philosophy anymore. It's measurable, trackable, and optimisable for true longevity.
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">Earlier</div>
                  <p className="text-sm text-muted-foreground">awareness of shifts in mood, energy and cycle so you can reflect and respond sooner</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">All 5</div>
                  <p className="text-sm text-muted-foreground">dimensions of the self integrated - body, mind, heart, soul, and the patterns connecting them</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-2">1 place</div>
                  <p className="text-sm text-muted-foreground">for biometrics, reflection, cycle tracking, manifestation, sleep review and self-support tools</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Core Features */}
          <div className="mb-20">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              Tools for reflection, not just measurement
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Therappi<sup className="text-[0.6em]">®</sup> is designed to help you see your patterns and choose your response - a self-reflection companion, not a clinical service.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* ROW 1-2: Core Tools */}
              {/* The Sanctuary */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Shield className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">The Sanctuary</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  An AI reflective companion inspired by multiple frameworks (CBT, psychodynamic, DBT, ACT) - personalised reflection conversations plus a signposting and self-support toolkit. A companion to complement, not replace, professional care.
                </p>
                <Badge variant="secondary">Reflective</Badge>
              </Card>

              {/* Nervous System Reset */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Heart className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Nervous System Reset</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Breathwork sequences, EFT tapping practices and somatic release exercises, alongside real-time HRV feedback from your WHOOP®. Support your nervous system state as part of a broader self-care practice.
                </p>
                <Badge variant="secondary">Practice-Based</Badge>
              </Card>

              {/* Quantum Journal */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <BookOpen className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Quantum Journal</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Write to process, release and integrate your experiences. The AI reflects patterns across your entries - surfacing themes and emotional shifts. Your personal archive, searchable and meaningful.
                </p>
                <Badge variant="secondary">Reflective</Badge>
              </Card>

              {/* Future Self System */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Star className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Future Self System</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Map where you're going. Navigate the route with intention. Embody the frequency of your future self. Take aligned action daily. This isn't goal-setting-it's identity-based reflection inspired by neuroscience research, designed for sustainable transformation and lasting performance.
                </p>
                <Badge variant="secondary">Transformative</Badge>
              </Card>

              {/* Manifestation Magnifier */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Sparkles className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Manifestation Magnifier</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Build personalised visualisation movies from your images, affirmations, and vision-then watch them daily. Rooted in neuroscience and future self research, this tool reprogrammes your subconscious mind to align behavior with aspiration. Because manifestation without action is just wishing.
                </p>
                <Badge variant="secondary">Neuroscience-Based</Badge>
              </Card>

              {/* Sleep & Dreams */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Moon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Sleep & Dreams Suite</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Analyse your sleep architecture with WHOOP integration. Journal dreams and receive AI-powered interpretation. Optimise rest with lunar cycle awareness. Access guided guided relaxation. Sleep is where your body heals and your subconscious speaks-Therappi helps you listen.
                </p>
                <Badge variant="secondary">Holistic</Badge>
              </Card>

              {/* ROW 3: Spiritual & Wellness */}
              {/* Rock Remedies */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Gem className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Rock Remedies®</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Explore 24 crystals mapped to chakras, emotions, and moon phases. Learn the psychology of crystal work-grounded in symbolism, intention-setting, and mindfulness. Discover which crystals support your current emotional state and reflection journey.
                </p>
                <Badge variant="secondary">Spiritual</Badge>
              </Card>

              {/* Meditation Medication */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Brain className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Meditation Medication®</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Access 60+ guided meditations for calm, sleep, reflection, manifestation and nervous system support. Each meditation tracks your before/after state and can be reviewed alongside your HRV data.
                </p>
                <Badge variant="secondary">Practice-Based</Badge>
              </Card>

              {/* Inflammation Hub */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Utensils className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Inflammation Hub</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Track your Inflammation Load Score through daily behaviours, sleep, stress, and nutrition. Understand how chronic inflammation impacts mood, energy, and longevity. Receive anti-inflammatory protocols and see correlations with mental health markers.
                </p>
                <Badge variant="secondary">Longevity</Badge>
              </Card>

              {/* ROW 4: Connection & Community */}
              {/* Connect & Grow */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Users className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Connect & Grow</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Your hub for meaningful connection-therapist collaboration, partner check-ins, accountability partnerships, and group challenges. No more healing in isolation. Build your support network and grow together with those who understand your journey.
                </p>
                <Badge variant="secondary">Community</Badge>
              </Card>

              {/* Therapist Connection */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <UserPlus className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Therapist Connection</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  If you work with a therapist or clinician, you can choose to share your wellbeing data, homework completion and progress between sessions. Receive personalised exercises assigned by your practitioner. All sharing is opt-in and consent-based.
                </p>
                <Badge variant="secondary">Collaboration</Badge>
              </Card>

              {/* Partner Connection */}
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <Heart className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Partner Connection</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Connect with your partner for couples check-ins, shared journaling, and relationship insights. Track intimacy, communication quality, and conflict patterns together. Receive AI-powered guidance for navigating challenges and deepening connection.
                </p>
                <Badge variant="secondary">Relational</Badge>
              </Card>
            </div>
          </div>

          {/* 5 User Personas Section - NEW */}
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              Who is Therappi for?
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Therappi meets you exactly where you are-whether you're managing anxiety, optimising performance, navigating hormonal transitions, deepening spiritual practice, or breaking sabotaging patterns. One platform. Every dimension of you.
            </p>
            
            <div className="space-y-8">
              {/* Persona 1: Sleep Issues & Behavioural Change - MOVED TO FIRST */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Moon className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">For the one breaking old patterns</h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      "I know what I should do-sleep better, drink less, eat well-but I keep falling back into the same cycles. Why can't I change? What's really driving these patterns?"
                    </p>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Your struggle:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Chronic sleep issues without understanding the root causes</li>
                        <li>• Self-sabotaging behaviours (alcohol, emotional eating, avoidance) that feel impossible to control</li>
                        <li>• Habit trackers that shame you for outcomes without addressing emotional drivers</li>
                        <li>• No support for understanding why you repeat the same destructive cycles</li>
                      </ul>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-primary">How Therappi transforms your experience:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Sleep & Dreams Suite</strong> with architecture analysis, AI dream interpretation, lunar optimisation, guided guided relaxation-transforming rest at its roots</li>
                        <li>• <strong>Pattern recognition AI</strong> that identifies behavioural triggers and emotional drivers-see how that third glass of wine shows up in tomorrow's mood and HRV</li>
                        <li>• <strong>Deeper self-reflection</strong> through The Sanctuary-address the "why" behind self-sabotage with evidence-based modalities</li>
                        <li>• <strong>Behaviour-biometric integration</strong>-track correlations between sleep, mood, nutrition, and habits to understand your unique system</li>
                        <li>• <strong>Compassionate accountability</strong>-predictive nudges before patterns spiral, not shame-based tracking after the damage is done</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Persona 2: Performance / Athletes / Biohackers - WHOOP Users */}
              <Card className="p-8 bg-gradient-to-br from-accent/5 to-primary/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Activity className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">For the high performer chasing longevity</h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      "My WHOOP tracks everything, but I'm still guessing. Why is my HRV tanking? How do I actually optimise recovery-not just physically, but mentally and emotionally too?"
                    </p>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Your struggle:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Drowning in biometric data but lacking actionable direction</li>
                        <li>• Optimising physical health whilst mental and emotional wellbeing suffer</li>
                        <li>• No visibility into how sleep, nutrition, HRV, mood, and performance connect</li>
                        <li>• Following protocols without understanding your body's unique responses</li>
                      </ul>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-primary">How Therappi transforms your experience:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>WHOOP integration</strong> that actually explains the "why"-closing the loop between biometrics, psychology, and behavior</li>
                        <li>• <strong>Multi-dimensional tracking</strong> reveals how your gut health affects HRV, how that third drink shows up in tomorrow's mood, how skipping breakfast impacts performance</li>
                        <li>• <strong>Gut-brain nutrition insights</strong> personalised to your biology-not generic advice from MyFitnessPal</li>
                        <li>• <strong>Real-time HRV biofeedback</strong> with breathwork and somatic tools to regulate your nervous system instantly</li>
                        <li>• <strong>True longevity optimisation</strong>-sustainable performance for your future self, not just crushing today's workout</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Persona 3: Mental Health / Worried Well */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Brain className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">For the anxious mind seeking peace</h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      "Meditation apps give me 10 minutes of calm, then I'm back to spiralling. I need to understand why I feel this way-and actually prevent it before it starts."
                    </p>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Your struggle:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Always reacting to anxiety, never getting ahead of it</li>
                        <li>• Tracking mood in one app, meditating in another, journalling elsewhere-nothing connects</li>
                        <li>• Cannot identify triggers or patterns before episodes escalate</li>
                        <li>• Therapy is expensive, and crisis support feels out of reach</li>
                      </ul>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-primary">How Therappi transforms your experience:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Pattern awareness</strong> helps you notice shifts in mood, energy and cycle earlier - so you can reflect and respond sooner</li>
                        <li>• <strong>The Sanctuary</strong> offers an AI reflective companion drawing on CBT, DBT and ACT-inspired frameworks - a personal reflection space, not clinical therapy</li>
                        <li>• <strong>The Soulprint Sequence™</strong> gives you a six-phase reflection framework: Regulate the nervous system, Recognise the pattern, Reflect on its origin, Release the old imprint, Reprogramme the new, Reconnect as a whole self</li>
                        <li>• <strong>Self-support toolkit</strong> with signposting to external crisis services, always accessible when things feel hard</li>
                        <li>• <strong>Everything in one place</strong> - journals, reflection notes, mood patterns, biometric context - finally integrated</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Persona 4: Spiritual Development / Manifestation */}
              <Card className="p-8 bg-gradient-to-br from-accent/5 to-primary/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Sparkles className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">For the soul craving alignment</h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      "I journal, pull oracle cards, track the moon-but it all feels separate from my mental health work. Can manifestation and psychology actually work together? Can spiritual practice be grounded in real results?"
                    </p>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Your struggle:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Spiritual practices feel disconnected from evidence-based psychology</li>
                        <li>• Manifestation apps offer pretty affirmations but lack biometric tracking and behavior integration</li>
                        <li>• No way to measure whether meditation, oracle work, or lunar rituals actually impact wellbeing</li>
                        <li>• Spiritual development and mental health support exist in separate silos</li>
                      </ul>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-primary">How Therappi transforms your experience:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Where Science Meets Soul</strong>-track how meditation impacts HRV, how moon phases correlate with mood, how oracle insights inform personal breakthroughs</li>
                        <li>• <strong>Manifestation Magnifier</strong> with neuroscience-inspired visualisation movies-manifestation grounded in behavior change, not wishful thinking</li>
                        <li>• <strong>Lunar and astrological integration</strong> that aligns your rituals with natural cycles, optimising timing for transformation</li>
                        <li>• <strong>Dream analysis with AI interpretation</strong>, oracle guidance, shadow work-all woven into reflective processing</li>
                        <li>• <strong>Measurable spiritual impact</strong>-see how your practices show up in biometrics, mood patterns, and real-life outcomes</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Persona 5: Women / Mothers / Hormonal Health - MOVED TO LAST */}
              <Card className="p-8 bg-gradient-to-br from-primary/5 to-accent/5">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 bg-gradient-primary rounded-full flex items-center justify-center">
                      <Heart className="h-8 w-8 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">For the woman riding hormonal tides</h3>
                    <p className="text-sm text-muted-foreground mb-4 italic">
                      "My mood crashes on day 24. My energy disappears in my luteal phase. Period trackers just log dates-but I need to understand why I feel this way and how to work with it, not against it."
                    </p>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">Your struggle:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Hormonal shifts feel chaotic and unsupported by generic wellness apps</li>
                        <li>• Period trackers focus on fertility, ignoring emotional and mental patterns</li>
                        <li>• Perimenopause, pregnancy, postpartum, IVF journeys lack integrated psychological care</li>
                        <li>• Physical symptoms, emotional states, and life stage transitions remain disconnected</li>
                      </ul>
                    </div>
                    <div className="bg-background/50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-primary">How Therappi transforms your experience:</h4>
                      <ul className="text-sm space-y-2">
                        <li>• <strong>Multi-journey tracking</strong> for menstrual cycles, perimenopause, menopause, pregnancy, postpartum, IVF-with psychological integration at every stage</li>
                        <li>• <strong>Hormone-mood correlation mapping</strong> that reveals exactly how your cycle drives mood, energy, sleep, and behavior-insights period trackers cannot offer</li>
                        <li>• <strong>Lunar and astrological alignment</strong> to synchronise with natural rhythms for deeper flow and ease</li>
                        <li>• <strong>Phase-specific reflection prompts</strong> using the Soulprint Sequence™ - tailored support for each cycle phase or life transition</li>
                        <li>• <strong>Body, mind, heart, soul integration</strong>-because healing the mind alone was never enough</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Journey-Specific Support - Women's Health Ecosystem */}
          <div className="mb-20">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
              Women's Health Ecosystem
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">
              Cycle, lunar, pregnancy, perimenopause, menopause - understand how hormonal rhythms influence your mood, energy, and emotional landscape. Align your biology with balance through personalised insights and practices.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Menstrual Journey */}
              <Card className="p-6">
                <Moon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Menstrual Cycle</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Phase-specific wellness optimisation</li>
                  <li>• Lunar synchronisation tracking</li>
                  <li>• Hormone-mood correlation mapping</li>
                  <li>• Predictive symptom alerts</li>
                </ul>
              </Card>

              {/* Peri/Menopause */}
              <Card className="p-6">
                <Heart className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Peri & Menopause</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Symptom pattern recognition</li>
                  <li>• Transition phase support</li>
                  <li>• Energetic alignment tools</li>
                  <li>• Community wisdom sharing</li>
                </ul>
              </Card>

              {/* Pregnancy/IVF */}
              <Card className="p-6">
                <Sparkles className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-bold mb-3">Pregnancy & IVF</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Trimester-specific guidance</li>
                  <li>• IVF cycle emotional support</li>
                  <li>• Postpartum wellness tracking</li>
                  <li>• Mother-baby bond tools</li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Telempathy Lab */}
          <div className="mb-20">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-accent/10 via-primary/5 to-accent/10">
              <h2 className="text-3xl sm:text-4xl font-bold text-center mb-6">
                Telempathy Lab: Where Science Meets Soul
              </h2>
              <p className="text-lg text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
                Bridge intuition and intellect through structured practices that cultivate inner perception and energetic awareness. Explore the psychology of symbolism, track energetic frequency, and learn to interpret intuitive data as a deeper form of emotional intelligence.
              </p>
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="bg-background/50 p-6 rounded-lg">
                  <h3 className="font-semibold mb-3 text-primary">Structured Exploration</h3>
                  <p className="text-sm text-muted-foreground">
                    Not vague "trust your gut" advice-practical frameworks for developing intuitive intelligence. Track patterns in oracle readings, dreams, synchronicities, and energetic awareness. Learn to discern genuine intuition from projection or wishful thinking.
                  </p>
                </div>
                <div className="bg-background/50 p-6 rounded-lg">
                  <h3 className="font-semibold mb-3 text-primary">Evidence-Based Spiritual Practice</h3>
                  <p className="text-sm text-muted-foreground">
                    See how meditation impacts HRV, how oracle insights correlate with personal breakthroughs, how lunar phases align with emotional patterns. Your spiritual practice becomes measurable, integrated, and grounded in psychological understanding.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Clinician Portal - B2B */}
          <div className="mb-20">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30">
              <div className="flex items-center justify-center mb-6">
                <UserPlus className="h-12 w-12 text-primary mr-4" />
                <h2 className="text-3xl sm:text-4xl font-bold">
                  For Therapists & Clinicians
                </h2>
              </div>
              <p className="text-lg text-center text-muted-foreground mb-8 max-w-3xl mx-auto">
                Enhance your practice with Therappi's Clinician Portal. See client-shared progress between sessions, assign exercises, review self-reported outcome measures and receive AI-generated session preparation summaries. All sharing is consent-based and GDPR-compliant.
              </p>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                <div className="bg-background/50 p-6 rounded-lg text-center">
                  <h3 className="font-semibold mb-3 text-primary">Client Progress Dashboard</h3>
                  <p className="text-sm text-muted-foreground">
                    Real-time visibility into daily check-ins, mood trends, sleep patterns, and homework completion-with full client consent and GDPR compliance.
                  </p>
                </div>
                <div className="bg-background/50 p-6 rounded-lg text-center">
                  <h3 className="font-semibold mb-3 text-primary">Homework Assignment</h3>
                  <p className="text-sm text-muted-foreground">
                    Assign specific exercises from our 60+ tool library. Clients receive notifications, complete tasks in-app, and you see completion rates and reflections.
                  </p>
                </div>
                <div className="bg-background/50 p-6 rounded-lg text-center">
                  <h3 className="font-semibold mb-3 text-primary">AI Session Preparation</h3>
                  <p className="text-sm text-muted-foreground">
                    Receive AI-generated briefs before each session-highlighting patterns, concerns, breakthroughs, and suggested focus areas based on client data.
                  </p>
                </div>
              </div>
              <div className="text-center mt-8">
                <Button variant="outline" size="lg" onClick={scrollToWaitlist} className="px-8">
                  Join Clinician Waitlist
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom Signup Sections - Side by Side */}
      <section id="signup-section" className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {/* Left: Be the First to Know */}
          <Card id="waitlist-section" className="p-8 bg-gradient-to-br from-card to-card/50 backdrop-blur-sm border-primary/20 h-fit scroll-mt-20">
            <h2 className="text-2xl font-bold mb-4">
              <EditableText contentKey="landing.waitlist.title.v2" defaultContent="Be The First To Know" category="landing" />
            </h2>
            <p className="text-muted-foreground mb-6">
              <EditableText contentKey="landing.waitlist.description" defaultContent="Join our waitlist to be notified the moment Therappi launches." category="landing" />
            </p>
            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
              <Input type="text" placeholder="Name:" value={name} onChange={e => setName(e.target.value)} required />
              <Input type="email" placeholder="Email:" value={email} onChange={e => setEmail(e.target.value)} required />
              <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-primary to-accent">
                {isSubmitting ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Joining...
                  </> : 'Join The Waitlist'}
              </Button>
            </form>
          </Card>

          {/* Right: WHOOP Pioneer Programme */}
          <Card id="pioneer-section" className="p-8 border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-accent/5 to-background shadow-xl h-fit scroll-mt-20">
            <div className="mb-6">
              <div className="flex justify-center mb-4">
                <img src={therappiWhoopLogo} alt="Therappi × WHOOP" className="h-16 object-contain" />
              </div>

              <h2 className="text-2xl font-bold mb-4 text-center bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                <EditableText contentKey="landing.whoop_pioneer.title" defaultContent="WHOOP User? Join Our Pioneer Programme" category="landing" />
              </h2>
              
              <p className="text-muted-foreground mb-6 text-center">
                <EditableText contentKey="landing.whoop_pioneer.description" defaultContent="Be among the first to experience the revolutionary integration of biometric tracking with mental wellness. Help shape the future of holistic health." category="landing" />
              </p>
            </div>

            <form onSubmit={handleWhoopPioneerSubmit} className="space-y-4">
              <Input type="email" placeholder="Your WHOOP-connected email" value={whoopEmail} onChange={e => setWhoopEmail(e.target.value)} required />
              <Button type="submit" disabled={isWhoopSubmitting} className="w-full bg-gradient-to-r from-primary to-accent">
                {isWhoopSubmitting ? <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Joining...
                  </> : 'Join The Pioneer Programme'}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Questions? Contact{' '}
              <a href="mailto:support@therappi.app?subject=WHOOP Pioneer Programme Inquiry" className="text-primary hover:underline font-medium">
                support@therappi.app
              </a>
            </p>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-background/80 backdrop-blur-sm py-8">
        <div className="container mx-auto px-6 text-center text-muted-foreground">
          <div className="flex justify-center mb-6">
            <img src={therappiLogo} alt="Therappi®" className="h-32 opacity-70" />
          </div>
          
          {/* Mandatory Disclaimer */}
          <div className="max-w-3xl mx-auto mb-6 p-4 bg-background/50 rounded-lg border border-border/30">
            <p className="text-xs leading-relaxed">
              <strong className="text-foreground">Important Notice:</strong> Therappi is a wellness tool designed to support emotional wellbeing, self-reflection, and personal growth. 
              It is not a medical device, mental health treatment, or a substitute for professional therapy. If you are experiencing a mental health crisis, 
              please contact emergency services or a crisis helpline immediately.
            </p>
          </div>

          <p className="text-sm">
            <EditableText contentKey="landing.footer.copyright" defaultContent="© 2025 Therappi. Where Science Meets Soul." category="landing" />
          </p>
        </div>
      </footer>
    </div>;
};
export default LandingPage;